import { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { formatDate } from "../lib/format";
import { ArrowLeft, ClipboardList, Package, Award, CheckCircle2, Clock, Factory, Truck, XCircle } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import dayjs from "dayjs";
import { aggregateByCanonical } from "../lib/brandColors";
import DateRangeFilter, { rangeFromPreset, filterOrdersByRange } from "../components/DateRangeFilter";

const BRAND_COLORS = ["#224A38", "#D35400", "#3E7B5B", "#E89B2D", "#3B75B2", "#B23B3B", "#5C6B61"];

const STATUS_META = {
  pending:       { label: "Pending",       color: "bg-yellow-50 text-yellow-700",   icon: Clock },
  in_production: { label: "In Production", color: "bg-purple-50 text-purple-700",   icon: Factory },
  dispatched:    { label: "Dispatched",    color: "bg-orange-50 text-orange-700",   icon: Truck },
  completed:     { label: "Completed",     color: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  cancelled:     { label: "Cancelled",     color: "bg-red-50 text-red-700",         icon: XCircle },
};

function StatMini({ icon: Icon, label, value }) {
  return (
    <div className="card-surface p-5">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="font-figtree text-sm text-ink-soft">{label}</div>
          <div className="font-outfit font-bold text-2xl text-ink mt-1 truncate">{value}</div>
        </div>
        <div className="h-10 w-10 rounded-xl bg-forest-soft text-forest grid place-items-center flex-shrink-0 ml-2">
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
      </div>
    </div>
  );
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ key: "all" });

  useEffect(() => {
    api.get(`/employees/${id}/detail`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  const rangeDates = rangeFromPreset(range.key, range.customFrom, range.customTo);
  const filteredOrders = useMemo(
    () => (data ? filterOrdersByRange(data.orders, rangeDates.from, rangeDates.to) : []),
    [data, rangeDates.from, rangeDates.to]
  );

  if (loading) return <div className="text-ink-soft font-figtree">Loading…</div>;
  if (!data) return <div className="text-ink-soft font-figtree">Not found</div>;

  const { employee } = data;
  const orders = filteredOrders;

  // Recompute stats from the filtered slice so KPIs match the selected range.
  // Cancelled orders count for `orders` (pipeline activity) but NOT for units sold.
  const activeOrders = orders.filter((o) => o.status !== "cancelled");
  const totals = {
    orders: orders.length,
    completed: orders.filter((o) => o.status === "completed").length,
    quantity: activeOrders.reduce((s, o) => s + (o.items || []).reduce((n, i) => n + Number(i.quantity || 0), 0), 0),
    top_buyer: (() => {
      const m = {};
      for (const o of orders) m[o.party_name] = (m[o.party_name] || 0) + 1;
      return Object.entries(m).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    })(),
  };

  // Monthly series (last 6 months) — units ordered
  const now = dayjs();
  const monthKeys = [];
  for (let i = 5; i >= 0; i--) monthKeys.push(now.subtract(i, "month").format("YYYY-MM"));
  const monthMap = Object.fromEntries(monthKeys.map((k) => [k, 0]));
  for (const o of activeOrders) {
    const k = dayjs(o.received_at).format("YYYY-MM");
    if (k in monthMap) monthMap[k] += (o.items || []).reduce((n, it) => n + (it.quantity || 0), 0);
  }
  const monthly = monthKeys.map((k) => ({ month: dayjs(k + "-01").format("MMM"), quantity: monthMap[k] }));

  // Brand mix — aggregate raw brand names into the 3 canonical brands so each
  // slice gets a clearly distinct colour (Dew Drop=orange, Hug Me=purple, Total Care=blue).
  const brandRows = [];
  for (const o of activeOrders) {
    for (const it of (o.items || [])) {
      brandRows.push({ brand: it.brand, quantity: Number(it.quantity || 0) });
    }
  }
  const brandMix = aggregateByCanonical(brandRows).map((b) => ({ name: b.brand, value: b.quantity, color: b.color }));

  return (
    <div data-testid="employee-detail-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-ink-soft hover:text-forest font-figtree font-medium mb-4 transition-colors duration-200">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <PageHeader
        title={employee.name}
        subtitle={`${employee.role || "Employee"} · ${employee.phone || "No phone"}`}
        action={<DateRangeFilter value={range} onChange={setRange} testidPrefix="employee-range" />}
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatMini icon={ClipboardList} label="Total Orders" value={totals.orders} />
        <StatMini icon={CheckCircle2} label="Completed" value={totals.completed} />
        <StatMini icon={Package} label="Units Sold" value={totals.quantity} />
        <StatMini icon={Award} label="Top Buyer" value={totals.top_buyer || "—"} />
      </div>

      {orders.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="card-surface p-6 lg:col-span-2">
            <h3 className="font-outfit font-semibold text-lg text-ink mb-1">Monthly Sales</h3>
            <p className="font-figtree text-sm text-ink-soft mb-4">Units sold over the last 6 months</p>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E0" vertical={false} />
                  <XAxis dataKey="month" stroke="#5C6B61" fontSize={12} />
                  <YAxis stroke="#5C6B61" fontSize={12} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E8E6E0", fontFamily: "Figtree" }} />
                  <Bar dataKey="quantity" fill="#224A38" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card-surface p-6">
            <h3 className="font-outfit font-semibold text-lg text-ink mb-1">Brand Mix</h3>
            <p className="font-figtree text-sm text-ink-soft mb-4">Which brands they move most</p>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={brandMix} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                    {brandMix.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E8E6E0", fontFamily: "Figtree" }} />
                  <Legend wrapperStyle={{ fontSize: 12, fontFamily: "Figtree" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {employee.notes && (
        <div className="card-surface p-5 mb-6">
          <div className="text-sm text-ink-soft font-figtree mb-1">Notes</div>
          <div className="font-figtree text-ink">{employee.notes}</div>
        </div>
      )}

      <h2 className="font-outfit font-semibold text-xl text-ink mb-4">Orders handled by {employee.name}</h2>
      <div className="card-surface overflow-hidden">
        {orders.length === 0 ? (
          <div className="p-8 text-center text-ink-soft font-figtree">No orders assigned yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sand/60">
                <tr>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Status</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Date</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Buyer</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Items</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const meta = STATUS_META[o.status] || STATUS_META.pending;
                  const Icon = meta.icon;
                  const items = o.items || [];
                  const totalUnits = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
                  const uniqueBrands = [...new Set(items.map((it) => it.brand))];
                  return (
                    <tr
                      key={o.id}
                      className="border-t border-line hover:bg-bone/60 cursor-pointer align-middle"
                      onClick={() => nav(`/orders/${o.id}`)}
                      data-testid={`employee-order-row-${o.id}`}
                    >
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-figtree font-semibold ${meta.color}`}>
                          <Icon className="h-3.5 w-3.5" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-figtree text-ink-soft text-sm whitespace-nowrap">{formatDate(o.received_at)}</td>
                      <td className="px-6 py-4 font-figtree text-ink">
                        <Link to={`/parties/${o.party_id}`} onClick={(e) => e.stopPropagation()} className="text-forest hover:underline font-medium">{o.party_name}</Link>
                      </td>
                      <td className="px-6 py-4 font-figtree text-ink">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-outfit font-semibold text-ink">
                            {items.length} item{items.length !== 1 ? "s" : ""}
                          </span>
                          <span className="text-xs text-ink-soft">·</span>
                          <span className="text-sm text-ink-soft">{totalUnits} units</span>
                        </div>
                        {uniqueBrands.length > 0 && (
                          <div className="text-xs text-ink-mute mt-0.5 truncate max-w-[280px]">
                            {uniqueBrands.slice(0, 3).join(", ")}
                            {uniqueBrands.length > 3 && ` +${uniqueBrands.length - 3}`}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
